import React, {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Grid,
  Button,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import {ArrowBack as ArrowBackIcon} from "@mui/icons-material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {useThunk} from "../hooks/useThunk";
import {showError, showSuccess} from "../utils/notification";
import {
  fetchHonorariumDetails,
  updateHonorariumApprovalStatus,
  updateHonorariumStatus,
} from "../store";
import {ROLES, BASE_URL} from "../utils/constants";
import axiosInstance from "../utils/axiosInstance";

const HonorariumDetails = () => {
  const dialogGridItemProps = {
    item: true,
    xl: 6,
    lg: 6,
    md: 6,
    sm: 12,
    xs: 12,
    sx: {
      width: {
        xl: "50%",
        lg: "50%",
        md: "50%",
        sm: "100%",
        xs: "100%",
      },
      p: 1,
    },
  };
  const {honorariumId} = useParams();
  const navigate = useNavigate();
  const {userData} = useSelector((state) => state.auth);
  const {selectedHonorarium, loading, error} = useSelector(
    (state) => state.honorarium
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentStatus: "",
    paymentDate: "",
    paymentMethod: "",
    remarks: "",
  });

  const [paymentApproveData, setPaymentApproveData] = useState({
    adminApproved: "",
    remarks: "",
  });

  const [doFetchHonorariumDetails] = useThunk(fetchHonorariumDetails);
  const [doUpdateStatus] = useThunk(updateHonorariumStatus);
  const [doUpdateAdminApprovalStatus] = useThunk(
    updateHonorariumApprovalStatus
  );

  useEffect(() => {
    doFetchHonorariumDetails(honorariumId);
  }, [doFetchHonorariumDetails, honorariumId]);

  const handleBack = () => {
    navigate("/dashboard/honorarium");
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleOpenApproveDialog = () => {
    setOpenApproveDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setPaymentData({
      paymentStatus: "",
      paymentDate: "",
      paymentMethod: "",
      remarks: "",
    });
  };

  const handleCloseApproveDialog = () => {
    setOpenApproveDialog(false);
    setPaymentApproveData({
      adminApproved: "",
      remarks: "",
    });
  };

  const handlePaymentChange = (e) => {
    const {name, value} = e.target;

    setPaymentData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePaymentApproveChange = (e) => {
    const {name, value} = e.target;

    setPaymentApproveData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    console.log(honorariumId, paymentData);

    try {
      const response = await doUpdateStatus({
        honorariumId,
        ...paymentData,
      });
      showSuccess(response?.message || "Payment status updated successfully");
      handleCloseDialog();
      navigate("/dashboard/honorarium");
    } catch (error) {
      showError(error.message);
    }
  };

  const handleApproveSubmit = async () => {
    console.log(honorariumId, paymentApproveData);
    try {
      const response = await doUpdateAdminApprovalStatus({
        honorariumId,
        ...paymentApproveData,
      });
      showSuccess(response?.message || "Payment status updated successfully");
      handleCloseApproveDialog();
      navigate("/dashboard/honorarium");
    } catch (error) {
      showError(error.message);
    }
  };

  if (loading) {
    return (
      <Box sx={{display: "flex", justifyContent: "center", mt: 4}}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedHonorarium) {
    return (
      <Box sx={{p: 2}}>
        <Alert severity="error">Honorarium record not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{p: 2}}>
      <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{mb: 3}}>
        Back to Honorarium List
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}>
        <Typography variant="h4" sx={{mb: 3}}>
          Honorarium Details
        </Typography>
        {selectedHonorarium?.canUpdatePayment && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenDialog}>
            {userData?.role === ROLES.ACCOUNTANT
              ? "Submit for Approval"
              : "Update Payment Status"}
          </Button>
        )}
        {selectedHonorarium?.canApprovePayment && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenApproveDialog}>
            Approve Honorarium
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 2}}>
          {error}
        </Alert>
      )}

      {selectedHonorarium?.pendingApprovalRequest && (
        <Alert severity="info" sx={{mb: 2}}>
          A pending approval request already exists for this honorarium payment.
        </Alert>
      )}

      {/* Manual debit confirmation banner — PROJECT_OVERVIEW §4.8.
          Visible to MD / SA when the honorarium is Paid + admin-Confirmed
          and the bank debit hasn't been recorded yet. */}
      {selectedHonorarium?.paymentStatus === "Paid" &&
        selectedHonorarium?.adminApproved === "Confirmed" &&
        selectedHonorarium?.paymentConfirmationStatus === "Pending" &&
        [ROLES.SUPER_ADMIN, ROLES.MANAGING_DIRECTOR].includes(
          userData?.role
        ) && (
          <Alert
            severity="warning"
            sx={{mb: 2}}
            action={
              <Box sx={{display: "flex", gap: 1}}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={async () => {
                    try {
                      await axiosInstance.patch(
                        `${BASE_URL}honorarium/${selectedHonorarium._id}/confirm-debit`,
                        {action: "Confirmed"}
                      );
                      showSuccess("Debit confirmed; bank balance updated");
                      window.location.reload();
                    } catch (e) {
                      showError(
                        e?.response?.data?.message || "Failed to confirm debit"
                      );
                    }
                  }}>
                  Confirm debit
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  onClick={async () => {
                    const reason =
                      window.prompt("Reason the payment bounced?") || "";
                    if (reason === null) return;
                    try {
                      await axiosInstance.patch(
                        `${BASE_URL}honorarium/${selectedHonorarium._id}/confirm-debit`,
                        {action: "Bounced", bounceReason: reason}
                      );
                      showSuccess("Marked as bounced");
                      window.location.reload();
                    } catch (e) {
                      showError(
                        e?.response?.data?.message ||
                          "Failed to mark as bounced"
                      );
                    }
                  }}>
                  Mark as bounced
                </Button>
              </Box>
            }>
            Payment recorded but bank debit not yet processed. Confirm once the
            bank confirms the payment cleared.
          </Alert>
        )}
      {selectedHonorarium?.paymentConfirmationStatus === "Bounced" && (
        <Alert severity="error" sx={{mb: 2}}>
          Payment bounced
          {selectedHonorarium?.bounceReason
            ? `: ${selectedHonorarium.bounceReason}`
            : ""}
          . The bank balance has not changed.
        </Alert>
      )}

      <Card sx={{mb: 3}}>
        <CardContent>
          <Typography variant="h6" sx={{mb: 2, color: "primary.main"}}>
            Employee Information
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold", width: "30%"}}>
                    Employee Name
                  </TableCell>
                  <TableCell>
                    {selectedHonorarium.userId.firstName}{" "}
                    {selectedHonorarium.userId.lastName}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Payment Status
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={
                        selectedHonorarium.paymentStatus === "Paid"
                          ? "success.main"
                          : "warning.main"
                      }>
                      {selectedHonorarium.paymentStatus}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Admin Approval
                  </TableCell>
                  <TableCell>
                    <Typography
                      color={
                        selectedHonorarium.adminApproved === "Confirmed"
                          ? "success.main"
                          : "warning.main"
                      }>
                      {selectedHonorarium.adminApproved}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{mt: 4, mb: 2, color: "primary.main"}}>
            Attendance Details
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold", width: "30%"}}>
                    Present Days
                  </TableCell>
                  <TableCell>{selectedHonorarium.totalPresentDays}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Absent Days
                  </TableCell>
                  <TableCell>{selectedHonorarium.totalAbsentDays}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{mt: 4, mb: 2, color: "primary.main"}}>
            Payment Details
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold", width: "30%"}}>
                    Total Honorarium
                  </TableCell>
                  <TableCell>₹{selectedHonorarium.totalHonorarium}</TableCell>
                </TableRow>
                {Number(selectedHonorarium?.perfectAttendanceBonus) > 0 && (
                  <TableRow sx={{bgcolor: "success.lighter"}}>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{fontWeight: "bold"}}>
                      Perfect-Attendance Bonus
                    </TableCell>
                    <TableCell>
                      +{selectedHonorarium.perfectAttendanceBonus} day
                      {selectedHonorarium.perfectAttendanceBonus > 1 ? "s" : ""} (no leave this month)
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Deduction
                  </TableCell>
                  <TableCell>₹{selectedHonorarium.deduction}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Daswand
                  </TableCell>
                  <TableCell>₹{selectedHonorarium.daswand}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Travelling Allowance
                  </TableCell>
                  <TableCell>
                    ₹{selectedHonorarium.travelling_allowance}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    M Travelling Allowance
                  </TableCell>
                  <TableCell>
                    ₹{selectedHonorarium.m_travelling_allowance}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Net Honorarium
                  </TableCell>
                  <TableCell sx={{fontWeight: "bold", color: "primary.main"}}>
                    ₹{selectedHonorarium.netHonorarium}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{mt: 4, mb: 2, color: "primary.main"}}>
            Additional Information
          </Typography>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold", width: "30%"}}>
                    Remarks
                  </TableCell>
                  <TableCell>{selectedHonorarium.remarks[0]}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Payment Method
                  </TableCell>
                  <TableCell>
                    {selectedHonorarium?.paymentMethod || "-"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{fontWeight: "bold"}}>
                    Payment Date
                  </TableCell>
                  <TableCell>
                    {selectedHonorarium?.paymentDate
                      ? new Date(
                          selectedHonorarium?.paymentDate
                        ).toLocaleDateString()
                      : "-"}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="xs">
        <DialogTitle>Update Payment Status</DialogTitle>
        <DialogContent sx={{pb: 0}}>
          <Grid container spacing={0} sx={{mt: 1}}>
            <Grid sx={{width: "100%", p: 1}}>
              <TextField
                select
                fullWidth
                required
                label="Payment Status"
                name="paymentStatus"
                value={paymentData.paymentStatus}
                onChange={handlePaymentChange}>
                <MenuItem value="Paid">Paid</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
            {paymentData?.paymentStatus === "Paid" && (
              <Grid {...dialogGridItemProps}>
                <TextField
                  select
                  fullWidth
                  label="Payment Method"
                  name="paymentMethod"
                  value={paymentData.paymentMethod}
                  onChange={handlePaymentChange}>
                  <MenuItem value="Bank">Bank</MenuItem>
                  <MenuItem value="Cash">Cash</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                </TextField>
              </Grid>
            )}
            {paymentData?.paymentStatus === "Paid" && (
              <Grid {...dialogGridItemProps}>
                <LocalizationProvider
                  dateAdapter={AdapterDateFns}
                  sx={{width: "100%"}}>
                  <DatePicker
                    label="Payment Date"
                    value={
                      paymentData.paymentDate
                        ? new Date(paymentData.paymentDate)
                        : null
                    }
                    onChange={(newValue) => {
                      setPaymentData((prev) => ({
                        ...prev,
                        paymentDate: newValue
                          ? newValue.toISOString().split("T")[0]
                          : "",
                      }));
                    }}
                    maxDate={new Date()}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required />
                    )}
                    sx={{width: "100%"}}
                  />
                </LocalizationProvider>
              </Grid>
            )}

            <Grid sx={{width: "100%", p: 1}}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                value={paymentData.remarks}
                onChange={handlePaymentChange}
                sx={{mb: {xs: 2, md: 0}}}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{pr: 4, pl: 4}}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={
              !paymentData.paymentStatus
                ? true
                : paymentData?.paymentStatus === "Paid" &&
                  !paymentData?.paymentMethod &&
                  !paymentData?.paymentDate
            }>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openApproveDialog}
        onClose={handleCloseApproveDialog}
        maxWidth="xs">
        <DialogTitle>Update Honorarium Status</DialogTitle>
        <DialogContent sx={{pb: 0}}>
          <Grid container spacing={0} sx={{mt: 1}}>
            <Grid sx={{width: "100%", p: 1}}>
              <TextField
                select
                fullWidth
                required
                label="Honorarium Status"
                name="adminApproved"
                value={paymentApproveData.adminApproved}
                onChange={handlePaymentApproveChange}>
                <MenuItem value="Confirmed">Confirmed</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
            <Grid sx={{width: "100%", p: 1}}>
              <TextField
                fullWidth
                label="Remarks"
                name="remarks"
                value={paymentApproveData.remarks}
                onChange={handlePaymentApproveChange}
                sx={{mb: {xs: 2, md: 0}}}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{pr: 4, pl: 4}}>
          <Button onClick={handleCloseApproveDialog}>Cancel</Button>
          <Button
            onClick={handleApproveSubmit}
            variant="contained"
            color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HonorariumDetails;
